/**
 * ExemplaarPopup — tweede-laag popup voor één exemplaar.
 * Alles bewerkbaar: exemplaarnummer (SetNr), status, opmerkingen, aankoopdatum,
 * waarde (nieuwwaarde), factuur (upload/download/verwijder) + reserveringen.
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Dialog, DialogContent, TextField, IconButton, MenuItem,
  Select, FormControl, CircularProgress, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { BACKEND_URL } from '../../utils/apiConfig';
import { formatDateISO } from '../../utils/weekUtils';
import { laatsteGebruik, datumKort } from './laatstGebruikt';
import { LOCATIES } from './catalogusFilters';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5', error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7', gold: '#d4943a', goldBg: '#fbf3e6',
  font: '"Manrope", system-ui, sans-serif', fontBody: '"Inter", system-ui, sans-serif',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", lineHeight: 1, display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', ...sx }}>{name}</span>
);

// "In gebruik" en "Reparatie" (oud) zijn legacy: niet meer kiesbaar (wel getoond als al gezet)
const STATUS_OPTIONS = ['Reparatie, wel bruikbaar', 'Reparatie, niet bruikbaar', 'Lange tijd afwezig', 'Marktplaats', 'Verkocht/gearchiveerd'];
const LEGACY_STATUSES = ['In gebruik', 'Reparatie'];
const STATUS_COLORS = {
  'In gebruik': T.emerald,
  'Reparatie, wel bruikbaar': T.amber,
  'Reparatie, niet bruikbaar': T.error,
  'Reparatie': T.error,
  'Lange tijd afwezig': T.outline,
  'Marktplaats': T.tertiary,
  'Verkocht/gearchiveerd': T.outlineVariant,
};

const CATEGORY_ICONS = {
  'Cameras': 'videocam', 'Drones': 'flight', 'Lens': 'camera', 'Monitors': 'monitor',
  'Statief': 'straighten', 'Geluid': 'mic', 'Grip': 'handyman', 'Licht': 'lightbulb',
  'Camera Accessoires': 'settings_input_component', 'Special effects': 'auto_awesome',
  'Studio equipment': 'storefront', 'Vervoer en karren': 'local_shipping', 'Kabels': 'cable',
};

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>{children}</Typography>
);

const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem', bgcolor: T.surface } };

export default function ExemplaarPopup({ open, material, onClose, onChanged, getMaterialReservations }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const fileRef = useRef(null);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const f = material?.fields || {};

  useEffect(() => {
    if (material) {
      setForm({
        SetNr: f.SetNr || '',
        Status: f.Status || '',
        Opslaglocatie: f.Opslaglocatie || '',
        Omschrijving: f.Omschrijving || '',
        Aankoopdatum: (f.Aankoopdatum || '').slice(0, 10),
        Kenteken: f.Kenteken || '',
        IMEI: f.IMEI || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [material]);

  if (!material) return null;

  const matId = material.id;
  const isVehicle = !!f['Is dit een voertuig?'] || f.Categorie === 'Vervoer en karren' || !!f.Kenteken;
  const reservations = getMaterialReservations ? getMaterialReservations(matId) : [];
  const today = formatDateISO(new Date());
  const byNewest = (a, b) => (b.start || '').localeCompare(a.start || ''); // nieuwste boven
  const upcoming = reservations.filter(r => (r.eind || r.start || '') >= today).sort(byNewest);
  const past = reservations.filter(r => (r.eind || r.start || '') < today).sort(byNewest);
  const laatst = laatsteGebruik(reservations, today);
  const afwezig = form.Status === 'Lange tijd afwezig';

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${BACKEND_URL}/Api/materialen/${matId}`, { fields: {
        SetNr: form.SetNr, Status: form.Status, Omschrijving: form.Omschrijving,
        Opslaglocatie: form.Opslaglocatie || null,
        Aankoopdatum: form.Aankoopdatum || null,
        ...(isVehicle ? { Kenteken: form.Kenteken || null, IMEI: form.IMEI || null } : {}),
      } });
      toast.success('Exemplaar opgeslagen');
      onChanged && onChanged();
      onClose();
    } catch (err) {
      toast.error('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${BACKEND_URL}/Api/materialen/${matId}/factuur`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Factuur geüpload');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Upload mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleDeleteInvoice = async () => {
    if (!(await bevestig({ titel: 'Factuur verwijderen?', tekst: 'De factuur wordt definitief van dit exemplaar verwijderd.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    setUploading(true);
    try {
      await axios.delete(`${BACKEND_URL}/Api/materialen/${matId}/factuur`);
      toast.success('Factuur verwijderd');
      onChanged && onChanged();
    } catch (err) {
      toast.error('Verwijderen mislukt: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  };

  const statusColor = STATUS_COLORS[form.Status] || T.outline;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '1rem', display: 'flex', flexDirection: 'column' } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${T.surfaceHigh}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '0.6rem', bgcolor: T.surfaceLow, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name={CATEGORY_ICONS[f.Categorie] || 'inventory_2'} size={22} sx={{ color: T.primary }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {f.ID || f.TypeText || 'Exemplaar'}
          </Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: statusColor }} />
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', fontWeight: 600, color: T.secondary }}>{form.Status || 'Geen status'}</Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small"><Icon name="close" size={20} sx={{ color: T.outline }} /></IconButton>
      </Box>

      <DialogContent sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5 }}>
        {/* Laatst gebruikt — waar was dit exemplaar het laatst? (wiki Robin, 6 okt 2026) */}
        {laatst && (
          <Box data-testid="laatst-gebruikt" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: '0.6rem', bgcolor: afwezig ? T.amberBg : T.surfaceLow }}>
            <Icon name={afwezig ? 'location_searching' : 'history'} size={17} sx={{ color: afwezig ? T.gold : T.outline, mt: 0.15 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.62rem', fontWeight: 700, color: afwezig ? T.gold : T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {laatst.lopend ? 'Nu mee op' : afwezig ? 'Kwijt? Laatst mee op' : 'Laatst mee op'}
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text, lineHeight: 1.3, wordBreak: 'break-word' }}>{laatst.draaidag}</Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', color: T.secondary }}>
                {datumKort(laatst.start)}{laatst.eind && laatst.eind.slice(0, 10) !== laatst.start.slice(0, 10) ? ` – ${datumKort(laatst.eind)}` : ''}
                {laatst.door ? ` · gereserveerd door ${laatst.door}` : ''}
              </Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2.5 }}>
          <Box>
            <FieldLabel>Exemplaarnummer</FieldLabel>
            <TextField size="small" fullWidth value={form.SetNr} onChange={(e) => set('SetNr', e.target.value)} sx={inputSx} />
          </Box>
          <Box>
            <FieldLabel>Status</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select value={form.Status} onChange={(e) => set('Status', e.target.value)} displayEmpty
                renderValue={(v) => v ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[v] || T.outline }} />
                    {v}{LEGACY_STATUSES.includes(v) ? ' (legacy)' : ''}
                  </Box>
                ) : <em>Geen</em>}
                sx={{ borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem', bgcolor: T.surface }}>
                <MenuItem value=""><em>Geen</em></MenuItem>
                {LEGACY_STATUSES.includes(form.Status) && <MenuItem value={form.Status} disabled sx={{ fontFamily: T.fontBody, fontSize: '0.85rem' }}>{form.Status} (legacy)</MenuItem>}
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem' }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_COLORS[s] || T.outline, mr: 1 }} />{s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>Opslaglocatie</FieldLabel>
            <FormControl size="small" fullWidth>
              <Select value={form.Opslaglocatie || ''} onChange={(e) => set('Opslaglocatie', e.target.value)} displayEmpty
                sx={{ borderRadius: '0.55rem', fontFamily: T.fontBody, fontSize: '0.85rem', bgcolor: T.surface }}>
                <MenuItem value=""><em>Onbekend</em></MenuItem>
                {form.Opslaglocatie && !LOCATIES.includes(form.Opslaglocatie) && <MenuItem value={form.Opslaglocatie}>{form.Opslaglocatie}</MenuItem>}
                {LOCATIES.map(l => <MenuItem key={l} value={l} sx={{ fontFamily: T.fontBody, fontSize: '0.85rem' }}>{l}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel>Aankoopdatum</FieldLabel>
            <TextField size="small" fullWidth type="date" value={form.Aankoopdatum} onChange={(e) => set('Aankoopdatum', e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx} />
          </Box>
        </Box>

        {isVehicle && (
          <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '0.7rem', bgcolor: T.surfaceLow, border: '1px solid rgba(45,51,53,0.06)' }}>
            <FieldLabel>Voertuiggegevens</FieldLabel>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ flex: 1, minWidth: 130 }}>
                <TextField size="small" fullWidth label="Kenteken" value={form.Kenteken || ''} onChange={(e) => set('Kenteken', e.target.value)} placeholder="bv. VKS-31-J" InputLabelProps={{ shrink: true }} sx={inputSx} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 130 }}>
                <TextField size="small" fullWidth label="IMEI (tracker)" value={form.IMEI || ''} onChange={(e) => set('IMEI', e.target.value)} placeholder="bv. 356938035643809" InputLabelProps={{ shrink: true }} sx={inputSx} />
              </Box>
            </Box>
          </Box>
        )}

        <Box sx={{ mb: 2.5 }}>
          <FieldLabel>Opmerkingen</FieldLabel>
          <TextField size="small" fullWidth multiline minRows={2} value={form.Omschrijving} onChange={(e) => set('Omschrijving', e.target.value)} sx={inputSx} />
        </Box>

        {/* Factuur */}
        <Box sx={{ mb: 2.5 }}>
          <FieldLabel>Factuur</FieldLabel>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" hidden onChange={handleFile} />
          {f.Factuur ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '0.6rem', bgcolor: T.surfaceLow, border: `1px solid ${T.surfaceHigh}` }}>
              <Icon name="description" size={20} sx={{ color: T.primary }} />
              <Typography component="a" href={f.Factuur} target="_blank" rel="noopener noreferrer" sx={{ flex: 1, minWidth: 0, fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.primary, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { textDecoration: 'underline' } }}>
                {f.FactuurBestandsnaam || 'Factuur openen'}
              </Typography>
              <Tooltip title="Vervangen" arrow><IconButton size="small" onClick={() => fileRef.current?.click()} disabled={uploading}><Icon name="upload" size={17} sx={{ color: T.outline }} /></IconButton></Tooltip>
              <Tooltip title="Verwijderen" arrow><IconButton size="small" onClick={handleDeleteInvoice} disabled={uploading}><Icon name="delete" size={17} sx={{ color: T.error }} /></IconButton></Tooltip>
            </Box>
          ) : (
            <Box onClick={() => !uploading && fileRef.current?.click()} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, p: 1.5, borderRadius: '0.6rem', border: `1.5px dashed ${T.outlineVariant}`, cursor: 'pointer', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow, borderColor: T.primary } }}>
              {uploading ? <CircularProgress size={16} sx={{ color: T.primary }} /> : <Icon name="upload_file" size={18} sx={{ color: T.outline }} />}
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600 }}>{uploading ? 'Bezig…' : 'Factuur uploaden (PDF of foto)'}</Typography>
            </Box>
          )}
        </Box>

        {/* Reserveringen */}
        <Box>
          <FieldLabel>Reserveringen ({reservations.length})</FieldLabel>
          {reservations.length === 0 ? (
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.outlineVariant, fontStyle: 'italic' }}>Geen reserveringen</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 320, overflow: 'auto', pr: 0.5 }}>
              {upcoming.length > 0 && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, color: T.emerald, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toekomstig ({upcoming.length})</Typography>
              )}
              {upcoming.map(r => (
                <Box key={r.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.25, py: 0.7, borderRadius: '0.45rem', bgcolor: T.emeraldBg }}>
                  <Icon name="calendar_today" size={14} sx={{ color: T.emerald, mt: 0.1 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', fontWeight: 600, color: T.text, lineHeight: 1.3, wordBreak: 'break-word' }}>{r.draaidag}</Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.66rem', color: T.secondary }}>{r.start}{r.eind && r.eind !== r.start ? ` – ${r.eind}` : ''}</Typography>
                  </Box>
                </Box>
              ))}
              {past.length > 0 && (
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.6rem', fontWeight: 800, color: T.outlineVariant, textTransform: 'uppercase', letterSpacing: '0.05em', mt: upcoming.length ? 0.75 : 0 }}>Verleden ({past.length})</Typography>
              )}
              {past.map(r => (
                <Box key={r.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 1.25, py: 0.6, borderRadius: '0.45rem', bgcolor: T.surfaceLow }}>
                  <Icon name="history" size={14} sx={{ color: T.outlineVariant, mt: 0.1 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', fontWeight: 600, color: T.secondary, lineHeight: 1.3, wordBreak: 'break-word' }}>{r.draaidag}</Typography>
                    <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.64rem', color: T.outlineVariant }}>{r.start}{r.eind && r.eind !== r.start ? ` – ${r.eind}` : ''}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${T.surfaceHigh}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }} />
        <Box onClick={onClose} sx={{ px: 2, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer', fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.8rem', color: T.secondary, '&:hover': { bgcolor: T.surfaceLow } }}>Annuleren</Box>
        <Box onClick={() => !saving && handleSave()} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: saving ? 'default' : 'pointer', background: `linear-gradient(135deg, ${T.tertiary} 0%, ${T.primary} 100%)`, color: '#fff', fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.8rem', boxShadow: '0 4px 12px rgba(99,89,131,0.25)', opacity: saving ? 0.7 : 1, '&:hover': { boxShadow: '0 6px 20px rgba(99,89,131,0.35)' } }}>
          {saving && <CircularProgress size={14} sx={{ color: '#fff' }} />}
          Opslaan
        </Box>
      </Box>
    </Dialog>
  );
}
